// Launch — доска запуска по дням (спека docs/superpowers/specs/2026-09-11-launch-screen-design.md): выбор лонча,
// дорожки и треки с барами по статусу, карточка, перетаскивание и клавиши по дням, строки, связи со стрелками,
// выделение рамкой и групповой сдвиг, создание и удаление лонча.
import { Copy, SquarePen, Trash2 } from 'lucide-react';
import { confirmDialog } from '@core/ui/confirm';
import { WriteOnly } from '@core/auth/access';
import { useCallback, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { data, useData, useSetting, useTable } from '@core/store';
import { Button } from '@core/ui/Button';
import { ScreenLoading } from '@core/ui/Loading';
import type { MenuItem } from '@core/ui/Menu';
import { addDays, daysBetween, today, type ISODate } from '@core/dates';
import { startWithin } from '../roadmap/shared/range';
import { BAR_HEIGHT, HEADER_ROW, LABEL_WIDTH, LAUNCH_DAY_WIDTH, barGeometry, barHeightFor, gridOf, rowHeight, snapDays } from '../roadmap/shared/geometry';
import { SplitGrid, type SplitRow } from '../roadmap/shared/SplitGrid';
import { reorderGhostRow, withGhostRow } from '../roadmap/shared/RowActions';
import { ghostRowIndex } from '../roadmap/shared/order';
import { ScreenToolbar, ViewMenu } from '../roadmap/shared/Toolbar';
import { useTitleMeasurer } from '../roadmap/shared/textWidth';
import { deleteQuestion, refocus, usePendingIds, useTaskDrag, useTaskKeys, useToast, useTodayX } from '../roadmap/shared/interaction';
import { applyPlanned } from '../roadmap/shared/apply';
import { resizeTarget } from '../roadmap/shared/stepping';
import { useAttention } from '../roadmap/shared/data';
import { SelectionChip, clearSelectionItem, groupShiftOf, useMarquee, useSelection } from '../roadmap/shared/selection';
import { LinksLayer, linkSegments, overrideOf, rowTops } from './LinksLayer';
import { LaunchHeader, LaunchLines } from './LaunchHeader';
import { LaunchModal } from './LaunchModal';
import { LaunchPicker } from './LaunchPicker';
import { LaunchRowGrid, LaunchRowLabel, type Drag, type LaunchQuick } from './LaunchRows';
import { LaunchTaskModal } from './LaunchTaskModal';
import { useLaunchRows } from './useLaunchRows';
import {
  LAUNCH_DEFAULT_DAYS, SHOWN_STATUSES, STATUS_LOOK, boardOf, byPosition, launchDraftOf, launchTaskDraftOf, newLaunchDraft, newLaunchTaskDraft, openBlockersByTask, planLaunchDuplicate, planLaunchGroupMove, planLaunchMove, planLaunchResize, planLaunchTaskSave,
  type Board, type Hint, type LaunchDraft, type LaunchTask, type LaunchTaskDraft, type Preview,
} from './planner';

const EMPTY_BOARD: Board = { rows: [], lanes: [], tracks: [], trackOrder: [], tasks: [] };
const taskNow = (id: string): LaunchTask | undefined => data.store.getState().tables.launch_task.get(id);
const launchTasksNow = (ids: Iterable<string>): LaunchTask[] => [...ids].map(taskNow).filter((t): t is LaunchTask => !!t);
const focusOf = (id: string) => `[data-launch-task="${id}"]`;

export function LaunchScreen() {
  const status = useData((s) => s.status);
  const launches = useTable('launch');
  const swimlanes = useTable('launch_swimlane');
  const tracks = useTable('launch_track');
  const tasks = useTable('launch_task');
  const links = useTable('launch_link');
  const list = useMemo(() => [...launches.values()].sort(byPosition), [launches]);
  const [activeId, setActiveId] = useSetting<string | null>('launch.active', null);
  const active = (activeId ? launches.get(activeId) : undefined) ?? list[0] ?? null;
  const board = useMemo(() => (active ? boardOf(active.id, swimlanes.values(), tracks.values(), tasks.values()) : EMPTY_BOARD), [active, swimlanes, tracks, tasks]);
  const { rows, lanes, tracks: trackList, trackOrder, tasks: boardTasks } = board;
  // диапазон — даты лонча, свойство доски (Глеб, 2026-09-11): правится в карточке лонча, показ с его начала
  const range = useMemo(() => ({ from: active?.start_date ?? today(), to: active?.end_date ?? addDays(today(), LAUNCH_DEFAULT_DAYS - 1) }), [active]);
  const grid = useMemo(() => gridOf(range.from, range.to, LAUNCH_DAY_WIDTH), [range]);
  const todayX = useTodayX(grid);
  const [fullTitles, setFullTitles] = useSetting<boolean>('launch.full_titles', false);
  const measure = useTitleMeasurer();
  const linkList = useMemo(() => [...links.values()], [links]); // массив, не итератор: связи читаются по многу раз
  const blockers = useMemo(() => openBlockersByTask(linkList, tasks), [linkList, tasks]);
  const visibleIds = useMemo(() => new Set(boardTasks.filter((t) => barGeometry(t, grid) !== null).map((t) => t.id)), [boardTasks, grid]);
  const selection = useSelection<LaunchTask>(visibleIds, launchTasksNow);
  const [hovered, setHovered] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const { fail } = useToast();
  const attention = useAttention();
  const pendingIds = usePendingIds();
  const { marquee } = useMarquee(gridRef, selection, 'data-launch-task');

  // ---------------------------------------------------------------- карточки
  const [draft, setDraft] = useState<LaunchTaskDraft | null>(null);
  const [launchDraft, setLaunchDraft] = useState<LaunchDraft | null>(null);
  const [hint, setHint] = useState<Hint | null>(null);
  const [quick, setQuick] = useState<Hint | null>(null); // быстрая задача: контур остаётся, под ним микро-форма
  const openTask = useCallback((t: LaunchTask) => { setDraft(launchTaskDraftOf(t, data.store.getState().tables.launch_link.values())); data.updatePresence({ editing: t.id }); }, []);
  const closeTask = useCallback(() => { setDraft(null); data.updatePresence({ editing: null }); }, []);
  const newTaskIn = useCallback((trackId: string, start: ISODate) => setDraft(newLaunchTaskDraft(trackId, start)), []);
  // быстрая задача лонча: три дня от дня под курсором, только название; Ctrl+Enter — та же заготовка в карточку
  const quickFor = useCallback((h: Hint): LaunchQuick => ({
    hint: h,
    onCancel: () => setQuick(null),
    onFull: (title) => { setQuick(null); setDraft({ ...newLaunchTaskDraft(h.track_id, h.start_date), title }); },
    onCreate: (title) => {
      setQuick(null);
      const plan = planLaunchTaskSave({ ...newLaunchTaskDraft(h.track_id, h.start_date), title }, undefined, () => crypto.randomUUID());
      if ('error' in plan) { fail(plan.error); return; }
      void data.applyChanges(plan.changes, { kind: 'create_launch_task', launch_task_id: plan.taskId }).then((r) => { if (!r.ok) fail(r.error); else refocus(`[data-launch-task="${plan.taskId}"]`); });
    },
  }), [fail]);

  // ---------------------------------------------------------------- перетаскивание и клавиши
  const latest = useRef({ grid, trackOrder });
  latest.current = { grid, trackOrder };
  const targetFor = useCallback((d: Drag, clientX: number, clientY: number, dx: number): Preview | null => {
    const g = latest.current.grid;
    const fresh = taskNow(d.task.id) ?? d.task;
    const days = snapDays(dx, 1, g.dayWidth);
    if (d.kind !== 'move') return { kind: 'resize', ...resizeTarget(fresh, d.kind, days, true) };
    if (d.group.length > 1) {
      const start = addDays(fresh.start_date, days);
      return { kind: 'move', track_id: fresh.track_id, start_date: start, end_date: addDays(start, daysBetween(fresh.start_date, fresh.end_date)) };
    }
    const rowEl = document.elementFromPoint(clientX, clientY)?.closest<HTMLElement>('[data-launch-row]');
    const key = rowEl?.dataset['launchRow'];
    if (!key || !key.startsWith('t:')) return null;
    const start = addDays(fresh.start_date, days);
    return { kind: 'move', track_id: key.slice(2), start_date: start, end_date: addDays(start, daysBetween(fresh.start_date, fresh.end_date)) };
  }, []);
  const finishDrag = useCallback(async (d: Drag, clientX: number, clientY: number, dx: number) => {
    const target = targetFor(d, clientX, clientY, dx);
    if (!target) return;
    const r = await applyPlanned(() => {
      const t = taskNow(d.task.id) ?? d.task;
      if (target.kind === 'move' && d.group.length > 1) return { changes: planLaunchGroupMove(launchTasksNow(d.group), daysBetween(t.start_date, target.start_date)), op: { kind: 'move_launch_tasks', launch_task_ids: d.group } };
      if (target.kind === 'move') return { changes: planLaunchMove(t, { track_id: target.track_id, start_date: target.start_date }), op: { kind: 'move_launch_task', launch_task_id: t.id } };
      const side = d.kind === 'resize-end' ? 'end' : 'start';
      return { changes: planLaunchResize(t, side, side === 'end' ? target.end_date : target.start_date), op: { kind: 'resize_launch_task', launch_task_id: t.id, side } };
    });
    if (!r.ok) fail(r.error);
  }, [targetFor, fail]);
  const { dragPreview, onBarPointerDown } = useTaskDrag<null, Preview, LaunchTask>({ gridRef, rowAttr: 'data-launch-row', table: 'launch_task', targetFor, onDrop: finishDrag, focusSelector: (d) => focusOf(d.task.id), selection });
  const groupShift = useMemo(() => groupShiftOf(dragPreview, taskNow), [dragPreview]);

  const duplicate = useCallback(async (t: LaunchTask) => {
    const id = crypto.randomUUID();
    const r = await applyPlanned(() => { const live = taskNow(t.id) ?? t; return { changes: planLaunchDuplicate(live, id), op: { kind: 'duplicate_launch_task', launch_task_id: live.id, new_id: id } }; });
    if (!r.ok) fail(r.error); else refocus(focusOf(id));
  }, [fail]);
  const remove = useCallback(async (t: LaunchTask) => {
    const group = selection.groupOf(t);
    if (!(await confirmDialog({ title: group ? 'Delete tasks' : 'Delete task', text: deleteQuestion(group ? group.length : t.title), yes: 'Delete', danger: true }))) return;
    const ids = group ? group.map((g) => g.id) : [t.id];
    const r = await data.applyChanges(ids.map((id) => ({ table: 'launch_task' as const, id, delete: true as const })), { kind: group ? 'delete_launch_tasks' : 'delete_launch_task', launch_task_ids: ids });
    if (!r.ok) fail(r.error);
  }, [fail, selection]);
  const menuFor = useCallback((t: LaunchTask): MenuItem[] => {
    const group = selection.groupOf(t);
    if (group) return [
      { label: `Delete ${group.length} tasks`, shortcut: 'Delete', onSelect: () => void remove(t), danger: true, icon: Trash2 },
      'sep',
      clearSelectionItem(selection),
    ];
    return [
      { label: 'Open', shortcut: 'Enter', onSelect: () => openTask(t), icon: SquarePen },
      { label: 'Duplicate after it', shortcut: 'Ctrl+D', onSelect: () => void duplicate(t), icon: Copy },
      'sep',
      { label: 'Delete', shortcut: 'Delete', onSelect: () => void remove(t), danger: true, icon: Trash2 },
    ];
  }, [openTask, duplicate, remove, selection]);
  // стрелки — по дням (Ctrl не нужен), вверх/вниз — соседний трек, Shift — длина; группа — все по дням
  const onBarKey = useTaskKeys<null, LaunchTask>({
    openTask, duplicate, remove, fail, focusSelector: (id) => focusOf(id), groupOf: selection.groupOf,
    planGroup: (group, { days }) => (days === 0 ? null : { changes: planLaunchGroupMove(group, Math.sign(days)), op: { kind: 'move_launch_tasks', launch_task_ids: group.map((g) => g.id) } }),
    plan: (taskId, { days, rows: dRows, grow }) => {
      const t = taskNow(taskId);
      if (!t) return null;
      if (grow !== 0) return { planned: { changes: planLaunchResize(t, 'end', addDays(t.end_date, Math.sign(grow))), op: { kind: 'resize_launch_task', launch_task_id: t.id, side: 'end' } }, ctx: null };
      const order = latest.current.trackOrder;
      const idx = order.indexOf(t.track_id);
      const trackId = order[Math.max(0, Math.min(order.length - 1, idx + dRows))] ?? t.track_id;
      return { planned: { changes: planLaunchMove(t, { track_id: trackId, start_date: addDays(t.start_date, Math.sign(days)) }), op: { kind: 'move_launch_task', launch_task_id: t.id } }, ctx: null };
    },
  });

  // ---------------------------------------------------------------- строки
  const rowsApi = useLaunchRows({ gridRef, launchId: active?.id ?? null, board, fail });
  const { laneReorder, trackReorder } = rowsApi;

  // высота бара и строки — один раз на трек; стрелки связей и слой поверх тела считаются по этим высотам
  const overTrack = dragPreview?.preview?.kind === 'move' ? dragPreview.preview.track_id : null;
  const layout = useMemo(() => {
    const out = new Map<string, { height: number; barH: number }>();
    for (const r of rows) {
      if (r.kind === 'swimlane') { out.set(r.key, { height: HEADER_ROW, barH: BAR_HEIGHT }); continue; }
      const barH = fullTitles ? barHeightFor(r.tasks, grid, measure) : BAR_HEIGHT;
      out.set(r.key, { height: rowHeight(r.subRows + (overTrack === r.id || hint?.track_id === r.id || quick?.track_id === r.id ? 1 : 0), barH), barH });
    }
    return out;
  }, [rows, grid, fullTitles, measure, overTrack, hint, quick]);
  const body = useMemo(() => rowTops(rows.map((r) => ({ key: r.key, height: layout.get(r.key)!.height }))), [rows, layout]);
  const segments = useMemo(() => {
    const override = overrideOf(dragPreview, groupShift?.days ?? null, rows, taskNow);
    return linkSegments(linkList, rows, tasks, grid, body.tops, (k) => layout.get(k)?.barH ?? BAR_HEIGHT, override);
  }, [dragPreview, groupShift, rows, linkList, tasks, grid, body, layout]);

  // ---------------------------------------------------------------- отрисовка
  if (status !== 'ready') return <ScreenLoading />;

  const laneIds = lanes.map((s) => s.id);
  const tasksInLane = (laneId: string) => rows.reduce((n, r) => n + (r.kind === 'track' && r.swimlaneId === laneId ? r.tasks.length : 0), 0);
  const splitRows: SplitRow[] = rows.map((row) => {
    const { height, barH } = layout.get(row.key)!;
    if (row.kind === 'swimlane') {
      return {
        key: row.key, height, className: `border-b border-gray-200 border-t border-t-gray-300 ${laneReorder.rowDrag?.id === row.id ? 'opacity-50' : ''}`,
        style: { background: `${row.color}1f` }, // дорожка целиком в своём цвете, как в старом
        label: <LaunchRowLabel row={row} onColor={(c) => void rowsApi.recolor(row.id, c)} onRename={(n) => void rowsApi.rename('launch_swimlane', row.id, n)} onAdd={() => void rowsApi.addTrack(row.id)} onDelete={() => void rowsApi.deleteRow('launch_swimlane', row.id, row.label, tasksInLane(row.id))} onHandle={(e) => laneReorder.onHandlePointerDown(row.id, e)} />,
        gridAttrs: { 'data-launch-row': row.key, 'data-swimlane': row.id },
        grid: <LaunchLines grid={grid} todayX={todayX} weekends={false} />,
      };
    }
    const isOver = overTrack === row.id;
    return {
      key: row.key, height, className: `border-b border-gray-200 ${trackReorder.rowDrag?.id === row.id ? 'opacity-50' : ''}`,
      label: <LaunchRowLabel row={row} onRename={(n) => void rowsApi.rename('launch_track', row.id, n)} onAdd={() => newTaskIn(row.id, startWithin(range))} onDelete={() => void rowsApi.deleteRow('launch_track', row.id, row.label, row.tasks.length)} onHandle={(e) => trackReorder.onHandlePointerDown(row.id, e)} />,
      labelClassName: 'bg-white', gridClassName: isOver ? 'bg-blue-50' : 'bg-white', gridAttrs: { 'data-launch-row': row.key, 'data-swimlane': row.swimlaneId },
      grid: (
        <LaunchRowGrid row={row} grid={grid} todayX={todayX} barH={barH} wrap={barH > BAR_HEIGHT} blockers={blockers} attention={attention} pendingIds={pendingIds}
          selected={selection.ids} onHover={setHovered}
          drag={dragPreview} groupShift={groupShift} hint={hint?.track_id === row.id ? hint : null} onHint={setHint}
          onBarPointerDown={(t, kind, e) => onBarPointerDown(t, null, kind, e)} onOpen={openTask} onKey={(t, e: KeyboardEvent<HTMLElement>) => onBarKey(t, null, e)} menuFor={menuFor}
          onNewAt={(date) => newTaskIn(row.id, date)} quick={quick?.track_id === row.id ? quickFor(quick) : null} onQuickAt={setQuick} />
      ),
    };
  });
  const firstTrack = trackOrder[0];
  // перестановка дорожки или трека: строка-призрак на месте приземления
  const laneDragged = laneReorder.rowDrag ? lanes.find((s) => s.id === laneReorder.rowDrag!.id) : undefined;
  const trackDragged = trackReorder.rowDrag ? trackList.find((t) => t.id === trackReorder.rowDrag!.id) : undefined;
  const lastTrackRow = rows.map((r) => r.kind).lastIndexOf('track');
  const ghostAt = laneDragged
    ? ghostRowIndex(laneReorder.rowDrag, laneIds, (id) => rows.findIndex((r) => r.kind === 'swimlane' && r.id === id), rows.length)
    : ghostRowIndex(trackReorder.rowDrag, trackOrder, (id) => rows.findIndex((r) => r.kind === 'track' && r.id === id), lastTrackRow + 1);
  const ghost = laneDragged ? reorderGhostRow(laneDragged.name, laneDragged.color) : trackDragged ? reorderGhostRow(trackDragged.name) : null;
  const bodyRows = ghost ? withGhostRow(splitRows, ghostAt, ghost) : splitRows;

  return (
    <div className="flex h-full">
      <div className="flex-1 flex flex-col min-w-0 bg-white rounded-lg shadow border border-gray-300 overflow-hidden">
        <ScreenToolbar
          actions={active && <>
            <WriteOnly><Button size="sm" onClick={() => { if (firstTrack) newTaskIn(firstTrack, startWithin(range)); }} disabled={!firstTrack} title={firstTrack ? 'New task in the first track' : 'Add a track first'}>+ Task</Button></WriteOnly>
            <Button size="sm" variant="secondary" onClick={() => void rowsApi.addSwimlane()}>+ Swimlane</Button>
          </>}
          what={<LaunchPicker list={list} active={active} onPick={setActiveId} onNew={() => setLaunchDraft(newLaunchDraft(today()))} onEdit={(l) => setLaunchDraft(launchDraftOf(l))} />}
          view={<ViewMenu items={[{ key: 'titles', label: 'Full titles', title: 'Rows grow so that long titles fit', checked: fullTitles, onChange: setFullTitles }]} />}
          status={<>
            <SelectionChip selection={selection} />
            <span className="flex items-center gap-2 text-xs" data-launch-legend title="Bar colours by status">
              {SHOWN_STATUSES.map((s) => <span key={s} className="flex items-center gap-1 font-medium" style={{ color: STATUS_LOOK[s].text }}><span className="inline-block w-2.5 h-2.5 rounded-full border" style={{ background: STATUS_LOOK[s].bg, borderColor: STATUS_LOOK[s].border }} aria-hidden />{STATUS_LOOK[s].glyph ? `${STATUS_LOOK[s].glyph} ` : ''}{STATUS_LOOK[s].label.toLowerCase()}</span>)}
            </span>
            <span className="text-sm text-gray-600" data-launch-count={boardTasks.length}>{boardTasks.length} tasks</span>
          </>}
        />
        {active ? (
          <SplitGrid gridRef={gridRef} grid={grid} labelWidth={LABEL_WIDTH} cornerLabel="Swimlane / track" rows={bodyRows} header={<LaunchHeader grid={grid} />} cursor={dragPreview || laneReorder.rowDrag || trackReorder.rowDrag ? 'grabbing' : undefined}
            scrollKey={`launch:${active.id}`} overlay={<LinksLayer segments={segments} width={grid.width} height={body.height} hovered={hovered} />} />
        ) : (
          <div className="p-6 text-gray-600">No launches yet — open the picker and create one.</div>
        )}
      </div>
      {draft && <LaunchTaskModal draft={draft} swimlanes={lanes} tracks={trackList} launchTasks={boardTasks} onClose={closeTask} />}
      {marquee}
      {launchDraft && <LaunchModal draft={launchDraft} onClose={() => setLaunchDraft(null)} onSaved={(id) => setActiveId(id)} />}
    </div>
  );
}
